import { useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Info as InfoIcon,
  Loader as LoaderIcon,
  Plus as PlusIcon,
  X as CloseIcon,
} from "lucide-react";
import {
  isConnectedSelectableWorkspacePhone,
  type WhatsAppConnectionResponse,
  type WhatsAppWorkspacePhoneOption,
} from "@multi-chat/shared";
import { attachWhatsAppPhone } from "../../../api/whatsapp-api.js";
import { ChannelMark } from "../../../components/channel/ChannelMark.js";
import { Badge } from "../../../components/ui/badge/index.js";
import { Button } from "../../../components/ui/button/index.js";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../../components/ui/tooltip/index.js";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "../../../components/ui/sheet/index.js";
import { AnimatePresence, motion, useReducedMotion } from "../../../lib/motion.js";
import { cn } from "../../../lib/utils.js";
import { TechnicalValue } from "../TechnicalValue.js";
import { WhatsAppPhoneAvatar } from "../ConnectedDetails/WhatsAppPhoneProfilePhotoDialog.js";
import { profilePictureUrlForPhone } from "../whatsapp-phone-avatar.js";
import { AddPhoneWizard } from "../add-phone/AddPhoneWizard.js";
import {
  existingPhoneWizardEntry,
  type AddPhoneWizardEntry,
} from "../add-phone/add-phone-wizard-state.js";

export type PhoneManagementSheetView = "home" | "wizard";

export interface PhoneManagementSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: number;
  connection: WhatsAppConnectionResponse;
  options: WhatsAppWorkspacePhoneOption[];
  optionsLoading: boolean;
  optionsError: string | null;
  busy: boolean;
  initialView?: PhoneManagementSheetView;
  initialEntry?: AddPhoneWizardEntry;
  unreachableMessage: string;
  onSwitch: (externalPhoneNumberId: string) => Promise<void>;
  onCompleted: (connection: WhatsAppConnectionResponse) => void;
  onReloadOptions: () => void;
}

function statusLabelKey(option: WhatsAppWorkspacePhoneOption): string {
  if (isConnectedSelectableWorkspacePhone(option)) {
    return "whatsapp.phoneManagement.status.connected";
  }
  if (option.kind === "attachable") {
    return "whatsapp.phoneManagement.status.readyToConnect";
  }
  if (option.registrationState === "registration_processing") {
    return "whatsapp.phoneManagement.status.processing";
  }
  if (option.registrationState === "needs_registration") {
    return "whatsapp.phoneManagement.status.registrationRequired";
  }
  return "whatsapp.phoneManagement.status.verificationRequired";
}

export function PhoneManagementSheet({
  open,
  onOpenChange,
  organizationId,
  connection,
  options,
  optionsLoading,
  optionsError,
  busy,
  initialView = "home",
  initialEntry = { kind: "new" },
  unreachableMessage,
  onSwitch,
  onCompleted,
  onReloadOptions,
}: PhoneManagementSheetProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const reduceMotion = useReducedMotion();
  const isRtl = i18n.dir() === "rtl";
  const titleId = useId();
  const descriptionId = useId();
  const [view, setView] = useState<PhoneManagementSheetView>(initialView);
  const [entry, setEntry] = useState<AddPhoneWizardEntry>(initialEntry);
  const [sessionNonce, setSessionNonce] = useState(0);
  const [rowBusyId, setRowBusyId] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [viewDirection, setViewDirection] = useState<1 | -1>(1);

  useEffect(() => {
    if (!open) {
      return;
    }
    setView(initialView);
    setEntry(initialEntry);
    setSessionNonce((value) => value + 1);
    setLocalError(null);
    setRowBusyId(null);
  }, [open, initialView, initialEntry]);

  const currentId = connection.workspacePhone?.externalPhoneNumberId ?? null;
  const connected = options.filter(isConnectedSelectableWorkspacePhone);
  const available = options.filter((option) => !isConnectedSelectableWorkspacePhone(option));
  const sessionKey = `${entry.kind}:${entry.kind === "existing" ? entry.externalPhoneNumberId : "new"}:${sessionNonce}`;

  const title = useMemo(() => {
    if (view === "wizard") {
      return entry.kind === "existing"
        ? t("whatsapp.phoneManagement.completeRegistration")
        : t("whatsapp.phoneManagement.addNumber");
    }
    return t("whatsapp.phoneManagement.title");
  }, [entry.kind, t, view]);

  const goHome = (): void => {
    setViewDirection(-1);
    setView("home");
    setEntry({ kind: "new" });
    setLocalError(null);
  };

  const startNew = (): void => {
    setViewDirection(1);
    setEntry({ kind: "new" });
    setSessionNonce((value) => value + 1);
    setView("wizard");
  };

  const startExisting = (option: WhatsAppWorkspacePhoneOption): void => {
    setViewDirection(1);
    setEntry(existingPhoneWizardEntry(option));
    setSessionNonce((value) => value + 1);
    setView("wizard");
  };

  const handleSwitch = async (option: WhatsAppWorkspacePhoneOption): Promise<void> => {
    if (option.externalPhoneNumberId === currentId || busy) {
      return;
    }
    setRowBusyId(option.externalPhoneNumberId);
    setLocalError(null);
    try {
      await onSwitch(option.externalPhoneNumberId);
    } catch {
      setLocalError(t("whatsapp.switchWorkspacePhone.optionsError"));
    } finally {
      setRowBusyId(null);
    }
  };

  const handleAttach = async (option: WhatsAppWorkspacePhoneOption): Promise<void> => {
    setRowBusyId(option.externalPhoneNumberId);
    setLocalError(null);
    try {
      const next = await attachWhatsAppPhone(organizationId, option.externalPhoneNumberId);
      onCompleted(next);
      onReloadOptions();
    } catch (error) {
      const code =
        error && typeof error === "object" && "code" in error
          ? String((error as { code: unknown }).code)
          : "";
      setLocalError(
        code === "WHATSAPP_PHONE_OWNED_BY_ANOTHER_ORGANIZATION"
          ? t("whatsapp.addPhone.errors.ownedByAnotherOrganization")
          : t("whatsapp.addPhone.errors.generic"),
      );
    } finally {
      setRowBusyId(null);
    }
  };

  const panelVariants = useMemo(() => {
    if (reduceMotion) {
      return {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
      };
    }
    const rtlFactor = isRtl ? -1 : 1;
    return {
      initial: (direction: 1 | -1) => ({
        opacity: 0,
        x: direction * rtlFactor * 16,
      }),
      animate: { opacity: 1, x: 0 },
      exit: (direction: 1 | -1) => ({
        opacity: 0,
        x: direction * rtlFactor * -16,
      }),
    };
  }, [isRtl, reduceMotion]);

  const panelTransition = reduceMotion
    ? { duration: 0 }
    : { duration: 0.22, ease: [0.22, 1, 0.36, 1] as const };

  const renderOption = (option: WhatsAppWorkspacePhoneOption): React.JSX.Element => {
    const isCurrent = option.externalPhoneNumberId === currentId;
    const pending = rowBusyId === option.externalPhoneNumberId;
    return (
      <li key={option.externalPhoneNumberId}>
        <div
          className={cn(
            "flex items-center gap-3 rounded-xl border border-line bg-surface px-3.5 py-3 transition-[border-color,background-color,box-shadow] duration-150",
            isCurrent && "border-primary/20 bg-primary/[0.04]",
          )}
        >
          <span
            className="relative size-10 shrink-0 overflow-hidden rounded-full border border-line bg-surface-muted"
            aria-hidden="true"
          >
            <WhatsAppPhoneAvatar
              src={profilePictureUrlForPhone(connection, option.externalPhoneNumberId)}
              iconSize={18}
            />
          </span>
          <div className="grid w-fit max-w-full min-w-0 justify-items-start gap-0.5 text-start">
            {option.verifiedName ? (
              <span className="text-[0.9rem] font-medium text-ink wrap-anywhere">
                {option.verifiedName}
              </span>
            ) : null}
            <TechnicalValue className="w-fit max-w-full font-medium tabular-nums wrap-anywhere text-ink">
              {option.phoneNumber ?? option.displayPhoneNumber}
            </TechnicalValue>
          </div>
          <div className="ms-auto flex shrink-0 flex-col items-end gap-1.5">
            {isCurrent ? (
              <Badge variant="success">{t("whatsapp.phoneManagement.current")}</Badge>
            ) : (
              <span className="text-[0.75rem] font-medium text-ink-subtle">
                {t(statusLabelKey(option))}
              </span>
            )}
            {isConnectedSelectableWorkspacePhone(option) && !isCurrent ? (
              <Button
                variant="outline"
                size="sm"
                className="h-8 px-2.5 text-[0.8125rem]"
                disabled={busy || pending}
                onClick={() => void handleSwitch(option)}
              >
                {pending
                  ? t("whatsapp.switchWorkspacePhone.switching")
                  : t("whatsapp.switchWorkspacePhone.confirm")}
              </Button>
            ) : null}
            {option.kind === "attachable" ? (
              <Button
                variant="default"
                size="sm"
                className="h-8 px-2.5 text-[0.8125rem]"
                disabled={busy || pending}
                onClick={() => void handleAttach(option)}
              >
                {pending
                  ? t("whatsapp.addPhone.actions.attaching")
                  : t("whatsapp.phoneManagement.connect")}
              </Button>
            ) : null}
            {option.kind === "registration_required" ? (
              <Button
                variant="outline"
                size="sm"
                className="h-8 px-2.5 text-[0.8125rem]"
                disabled={busy || pending}
                onClick={() => startExisting(option)}
              >
                {t("whatsapp.switchWorkspacePhone.registerAction")}
              </Button>
            ) : null}
          </div>
        </div>
      </li>
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="end"
        data-testid="whatsapp-phone-management-sheet"
        data-side="end"
        className="h-full max-h-dvh w-full max-w-none gap-0 overflow-hidden bg-surface p-0 text-ink max-md:inset-0 data-[state=closed]:animate-sheet-out-end md:w-[min(100%,33.75rem)] md:max-w-[min(100%,33.75rem)] rtl:data-[state=closed]:animate-sheet-out-start"
        overlayClassName="data-[state=closed]:animate-overlay-out"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
      >
        <header className="flex shrink-0 items-start justify-between gap-3 border-b border-line px-5 py-4">
          <div className="flex min-w-0 items-start gap-3">
            <ChannelMark platformType="WHATSAPP" size="sm" />
            <div className="grid min-w-0 gap-0.5 text-start">
              <div className="flex min-w-0 items-center gap-1">
                <SheetTitle id={titleId} className="text-start text-base font-semibold">
                  {title}
                </SheetTitle>
                {view === "home" ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        className="inline-grid size-6 shrink-0 place-items-center rounded-full text-ink-muted hover:bg-surface-muted hover:text-ink focus-visible:bg-surface-muted"
                        aria-label={t("whatsapp.phoneManagement.about")}
                      >
                        <InfoIcon size={15} aria-hidden="true" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-72 text-start text-pretty leading-snug">
                      {t("whatsapp.phoneManagement.description")}
                    </TooltipContent>
                  </Tooltip>
                ) : null}
              </div>
              <SheetDescription
                id={descriptionId}
                className="text-start text-[0.8125rem] leading-snug text-ink-muted"
              >
                {view === "home"
                  ? t("whatsapp.phoneManagement.subtitle")
                  : t("whatsapp.addPhone.dialogDescription")}
              </SheetDescription>
            </div>
          </div>
          <Button
            variant="ghost"
            className="size-8 shrink-0 p-0"
            onClick={() => onOpenChange(false)}
            aria-label={t("whatsapp.phoneManagement.close")}
          >
            <CloseIcon className="size-4" />
          </Button>
        </header>

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-5 py-4">
          <AnimatePresence mode="wait" initial={false} custom={viewDirection}>
            {view === "wizard" ? (
              <motion.div
                key={`wizard:${sessionKey}`}
                className="flex min-h-0 flex-1 flex-col"
                custom={viewDirection}
                variants={panelVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={panelTransition}
              >
                <AddPhoneWizard
                  open={open}
                  onOpenChange={(next) => {
                    if (!next) {
                      goHome();
                    }
                  }}
                  organizationId={organizationId}
                  entry={entry}
                  sessionKey={sessionKey}
                  variant="plain"
                  unreachableMessage={unreachableMessage}
                  onCompleted={(next) => {
                    onCompleted(next);
                    onReloadOptions();
                  }}
                  onBack={goHome}
                />
              </motion.div>
            ) : (
              <motion.div
                key="home"
                className="grid gap-6"
                custom={viewDirection}
                variants={panelVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={panelTransition}
              >
                {connection.workspacePhone ? (
                  <section className="grid gap-2">
                    <h3 className="m-0 text-[0.7rem] font-semibold tracking-[0.06em] text-ink-subtle uppercase">
                      {t("whatsapp.phoneManagement.currentNumber")}
                    </h3>
                    <div className="flex items-center gap-3 rounded-xl border border-primary/20 bg-primary/[0.04] px-3.5 py-3">
                      <span
                        className="relative size-10 shrink-0 overflow-hidden rounded-full border border-line bg-surface"
                        aria-hidden="true"
                      >
                        <WhatsAppPhoneAvatar
                          src={profilePictureUrlForPhone(
                            connection,
                            connection.workspacePhone.externalPhoneNumberId,
                          )}
                          iconSize={18}
                        />
                      </span>
                      <div className="grid w-fit max-w-full min-w-0 justify-items-start gap-0.5 text-start">
                        {connection.workspacePhone.displayName ? (
                          <p className="m-0 text-[0.9rem] font-medium text-ink wrap-anywhere">
                            {connection.workspacePhone.displayName}
                          </p>
                        ) : null}
                        <TechnicalValue className="w-fit max-w-full font-medium tabular-nums text-ink">
                          {connection.workspacePhone.phoneNumber}
                        </TechnicalValue>
                      </div>
                      <Badge variant="success" className="ms-auto">
                        {t("whatsapp.phoneManagement.current")}
                      </Badge>
                    </div>
                  </section>
                ) : null}

                <section className="grid gap-2">
                  <h3 className="m-0 text-[0.7rem] font-semibold tracking-[0.06em] text-ink-subtle uppercase">
                    {t("whatsapp.switchWorkspacePhone.connectedSection")}
                  </h3>
                  {optionsLoading ? (
                    <p className="m-0 flex items-center gap-2 text-[0.875rem] text-ink-muted">
                      <LoaderIcon className="size-4 animate-spin" aria-hidden="true" />
                      {t("whatsapp.switchWorkspacePhone.loadingOptions")}
                    </p>
                  ) : (
                    <ul className="m-0 grid list-none gap-2 p-0">{connected.map(renderOption)}</ul>
                  )}
                </section>

                <section className="grid gap-2">
                  <h3 className="m-0 text-[0.7rem] font-semibold tracking-[0.06em] text-ink-subtle uppercase">
                    {t("whatsapp.switchWorkspacePhone.availableSection")}
                  </h3>
                  {optionsError ? (
                    <p className="m-0 text-[0.875rem] text-error" role="alert">
                      {optionsError}
                    </p>
                  ) : available.length === 0 && !optionsLoading ? (
                    <p className="m-0 text-[0.875rem] text-ink-muted">
                      {t("whatsapp.phoneManagement.noAvailable")}
                    </p>
                  ) : (
                    <ul className="m-0 grid list-none gap-2 p-0">{available.map(renderOption)}</ul>
                  )}
                </section>

                {localError ? (
                  <p className="m-0 text-[0.875rem] text-error" role="alert">
                    {localError}
                  </p>
                ) : null}

                <Button variant="default" className="w-full" onClick={startNew}>
                  <PlusIcon className="size-4" aria-hidden="true" />
                  {t("whatsapp.phoneManagement.addNumber")}
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </SheetContent>
    </Sheet>
  );
}
